import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Plus,
  Truck,
  Wrench,
} from 'lucide-react';
import type { FuhrparkDriver, FuhrparkListResponse, VehicleListItem } from '../types';
import { daysText, formatIsoDate, formatKm } from '../format';
import { vehiclePhotoUrl } from '../api';
import { VehicleStatusBadge } from './FuhrparkStatusBadge';
import {
  DriverAvatar,
  FUHRPARK_BTN_GREEN,
  FUHRPARK_TABLE_PAGE_SIZE,
  FuhrparkFilterChip,
  FuhrparkListPagination,
  FuhrparkSearchInput,
  FuhrparkSortableHeader,
  IconKpi,
  PlateBadge,
  type FuhrparkSortDir,
} from './fuhrparkUi';


type FilterKey = 'all' | 'active' | 'due' | 'workshop' | 'inactive';
type SortKey = 'model' | 'plate' | 'driver' | 'mileage' | 'next' | 'status';
type SortDir = FuhrparkSortDir;

function matchesFilter(item: VehicleListItem, filter: FilterKey): boolean {
  if (filter === 'active') return item.status === 'active';
  if (filter === 'due') return item.has_due;
  if (filter === 'workshop') return item.status === 'workshop';
  if (filter === 'inactive') return item.status === 'inactive';
  return true;
}

function matchesSearch(item: VehicleListItem, q: string): boolean {
  if (!q) return true;
  const hay = [item.model, item.brand, item.plate, item.driver?.name ?? '']
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export function FuhrparkListView({
  data,
  canEdit,
  createDisabled = false,
  createHint = null,
  onOpen,
  onCreate,
}: {
  data: FuhrparkListResponse;
  canEdit: boolean;
  createDisabled?: boolean;
  createHint?: React.ReactNode;
  onOpen: (id: string) => void;
  onCreate: () => void;
}) {
  const [filter, setFilter] = React.useState<FilterKey>('all');
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [sort, setSort] = React.useState<{ key: SortKey; dir: SortDir }>({ key: 'model', dir: 'asc' });
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const rows = React.useMemo(() => {
    const filtered = data.items.filter(
      (item) => matchesFilter(item, filter) && matchesSearch(item, debouncedSearch),
    );
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => compareVehicles(a, b, sort.key) * dir);
  }, [data.items, filter, debouncedSearch, sort]);

  React.useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch, sort]);

  const pageCount = Math.max(1, Math.ceil(rows.length / FUHRPARK_TABLE_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = rows.slice((safePage - 1) * FUHRPARK_TABLE_PAGE_SIZE, safePage * FUHRPARK_TABLE_PAGE_SIZE);

  const counts: Record<FilterKey, number> = {
    all: data.items.length,
    active: data.items.filter((v) => v.status === 'active').length,
    due: data.items.filter((v) => v.has_due).length,
    workshop: data.items.filter((v) => v.status === 'workshop').length,
    inactive: data.items.filter((v) => v.status === 'inactive').length,
  };

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const chips: Array<{ key: FilterKey; label: string }> = [
    { key: 'all', label: 'Alle' },
    { key: 'active', label: 'Aktiv' },
    { key: 'due', label: 'Wartung fällig' },
    { key: 'workshop', label: 'In Werkstatt' },
    { key: 'inactive', label: 'Inaktiv' },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="flex w-full flex-col gap-5 px-4 py-4 md:px-6 md:py-5 lg:px-8">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <IconKpi icon={Truck} iconClass="bg-blue-50 text-pipedrive-blue" label="Fahrzeuge gesamt" value={data.stats.total.toLocaleString('de-DE')} />
          <IconKpi icon={CheckCircle2} iconClass="bg-emerald-50 text-emerald-600" label="Aktiv im Einsatz" value={data.stats.active.toLocaleString('de-DE')} />
          <IconKpi icon={AlertTriangle} iconClass="bg-amber-50 text-amber-600" label="Wartung fällig" value={data.stats.due.toLocaleString('de-DE')} />
          <IconKpi icon={Wrench} iconClass="bg-violet-50 text-violet-600" label="In der Werkstatt" value={data.stats.workshop.toLocaleString('de-DE')} />
        </div>

        <section className="min-w-0">
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-app-text">Anstehende Wartungen</h2>
            {data.urgent.length ? (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-50 px-1.5 text-[11px] font-semibold text-rose-700">
                {data.urgent.length}
              </span>
            ) : null}
          </div>
          {data.urgent.length === 0 ? (
            <p className="rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm text-app-text-subtle">
              Keine anstehenden Wartungen.
            </p>
          ) : (
            <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 md:grid md:grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] md:overflow-visible md:snap-none">
              {data.urgent.map((u) => (
                <button
                  key={`${u.vehicle_id}-${u.reminder_id}`}
                  type="button"
                  onClick={() => onOpen(u.vehicle_id)}
                  className={`w-[min(100%,18rem)] shrink-0 snap-start rounded-xl border border-app-border bg-app-surface p-3 text-left md:w-auto ${
                    u.due_state === 'overdue' ? 'border-l-[3px] border-l-rose-500' : 'border-l-[3px] border-l-amber-400'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-app-text">{u.label}</p>
                    <span
                      className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium ${
                        u.due_state === 'overdue' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-800'
                      }`}
                    >
                      {daysText(u.days)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-app-text">{u.model}</p>
                  <p className="font-mono text-xs text-app-text-subtle">{u.plate}</p>
                  <p className="mt-1 text-xs text-app-text-subtle">
                    Fällig: {formatIsoDate(u.due_on)} · Fahrer: {u.driver_name || '—'}
                  </p>
                </button>
              ))}
            </div>
          )}
        </section>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {chips.map((c) => (
              <FuhrparkFilterChip
                key={c.key}
                label={c.label}
                count={counts[c.key]}
                active={filter === c.key}
                onClick={() => setFilter(c.key)}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {createHint}
            <FuhrparkSearchInput value={search} onChange={setSearch} />
            {canEdit ? (
              <button
                type="button"
                className={`${FUHRPARK_BTN_GREEN} disabled:opacity-40`}
                onClick={onCreate}
                disabled={createDisabled}
              >
                <Plus className="h-4 w-4" />
                Fahrzeug
              </button>
            ) : null}
          </div>
        </div>

        <div className="space-y-2 md:hidden">
          {pagedRows.length === 0 ? (
            <p className="rounded-xl border border-app-border bg-app-surface px-4 py-8 text-center text-sm text-app-text-subtle">
              Keine Fahrzeuge gefunden.
            </p>
          ) : (
            pagedRows.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => onOpen(v.id)}
                className="flex w-full gap-3 rounded-xl border border-app-border bg-app-surface p-3 text-left"
              >
                <VehicleThumb id={v.id} hasPhoto={v.has_photo} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-app-text">{v.model || 'Fahrzeug'}</p>
                      <p className="font-mono text-xs text-app-text-subtle">{v.plate || '—'}</p>
                    </div>
                    <VehicleStatusBadge status={v.status} hasDue={v.has_due} />
                  </div>
                  <p className="mt-1 truncate text-xs text-app-text-subtle">
                    {v.driver?.name ?? 'Nicht zugewiesen'} · {formatKm(v.mileage)}
                  </p>
                  <p className="mt-0.5 text-xs text-app-text-subtle">
                    {v.next_reminder ? `${v.next_reminder.label} · ${daysText(v.next_reminder.days)}` : 'keine offene Wartung'}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>

        <div className="hidden overflow-hidden rounded-xl border border-app-border bg-app-surface md:block">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-[24%]" />
              <col className="w-[15%]" />
              <col className="w-[18%]" />
              <col className="w-[12%]" />
              <col className="w-[18%]" />
              <col className="w-[13%]" />
              <col className="w-10" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-app-surface-muted text-left">
              <tr>
                <FuhrparkSortableHeader label="Fahrzeug" active={sort.key === 'model'} dir={sort.dir} onSort={() => toggleSort('model')} />
                <FuhrparkSortableHeader label="Kennzeichen" active={sort.key === 'plate'} dir={sort.dir} onSort={() => toggleSort('plate')} />
                <FuhrparkSortableHeader label="Fahrer" active={sort.key === 'driver'} dir={sort.dir} onSort={() => toggleSort('driver')} />
                <FuhrparkSortableHeader label="km-Stand" active={sort.key === 'mileage'} dir={sort.dir} onSort={() => toggleSort('mileage')} />
                <FuhrparkSortableHeader label="Nächste Wartung" active={sort.key === 'next'} dir={sort.dir} onSort={() => toggleSort('next')} />
                <FuhrparkSortableHeader label="Status" active={sort.key === 'status'} dir={sort.dir} onSort={() => toggleSort('status')} />
                <th className="w-10 px-2" aria-hidden />
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-app-text-subtle">
                    Keine Fahrzeuge gefunden.
                  </td>
                </tr>
              ) : (
                pagedRows.map((v) => (
                  <tr
                    key={v.id}
                    className="cursor-pointer border-t border-app-border hover:bg-app-hover"
                    onClick={() => onOpen(v.id)}
                  >
                    <td className="min-w-0 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <VehicleThumb id={v.id} hasPhoto={v.has_photo} compact />
                        <div className="min-w-0">
                          <p className="truncate font-medium text-app-text">{v.model || 'Fahrzeug'}</p>
                          <p className="truncate text-xs text-app-text-subtle">
                            {v.brand}
                            {v.year ? ` · ${v.year}` : ''}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="min-w-0 px-4 py-3">
                      <PlateBadge plate={v.plate} />
                    </td>
                    <td className="min-w-0 px-4 py-3">
                      <DriverCell driver={v.driver} />
                    </td>
                    <td className={`min-w-0 px-4 py-3 ${v.mileage ? 'text-app-text-muted' : 'text-app-text-subtle'}`}>
                      {formatKm(v.mileage)}
                    </td>
                    <td className="min-w-0 px-4 py-3">
                      {v.next_reminder ? (
                        <>
                          <p className="truncate text-app-text">{v.next_reminder.label}</p>
                          <p className={`text-xs ${v.next_reminder.due_state === 'overdue' ? 'text-rose-600' : v.next_reminder.due_state === 'soon' ? 'text-amber-700' : 'text-app-text-subtle'}`}>
                            {daysText(v.next_reminder.days)}
                          </p>
                        </>
                      ) : (
                        <p className="text-app-text-subtle">Keine offen</p>
                      )}
                    </td>
                    <td className="min-w-0 px-4 py-3">
                      <VehicleStatusBadge status={v.status} hasDue={v.has_due} />
                    </td>
                    <td className="px-2 py-3 text-slate-400">
                      <ChevronRight className="h-4 w-4" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <FuhrparkListPagination total={rows.length} page={safePage} pageSize={FUHRPARK_TABLE_PAGE_SIZE} onPageChange={setPage} />
        </div>

        <div className="md:hidden">
          <FuhrparkListPagination total={rows.length} page={safePage} pageSize={FUHRPARK_TABLE_PAGE_SIZE} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
}

function compareVehicles(a: VehicleListItem, b: VehicleListItem, key: SortKey): number {
  const locale = (x: string, y: string) => x.localeCompare(y, 'de', { sensitivity: 'base' });
  if (key === 'model') return locale(`${a.brand} ${a.model}`.trim(), `${b.brand} ${b.model}`.trim());
  if (key === 'plate') return locale(a.plate, b.plate);
  if (key === 'driver') return locale(a.driver?.name ?? '', b.driver?.name ?? '');
  if (key === 'mileage') return a.mileage - b.mileage;
  if (key === 'next') return (a.next_reminder?.days ?? 9999) - (b.next_reminder?.days ?? 9999);
  return locale(a.status, b.status);
}

function DriverCell({ driver }: { driver: FuhrparkDriver | null }) {
  if (!driver) return <span className="text-app-text-subtle">Nicht zugewiesen</span>;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <DriverAvatar driver={driver} />
      <span className="truncate text-app-text-muted">{driver.name}</span>
    </div>
  );
}

function VehicleThumb({ id, hasPhoto, compact }: { id: string; hasPhoto: boolean; compact?: boolean }) {
  return (
    <div
      className={`shrink-0 overflow-hidden rounded-md bg-app-surface-muted ${compact ? 'h-9 w-14' : 'h-14 w-[4.5rem]'}`}
    >
      {hasPhoto ? (
        <img src={vehiclePhotoUrl(id)} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">Foto</div>
      )}
    </div>
  );
}
